import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputBase,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AdjustIcon from '@mui/icons-material/Adjust';
import EventIcon from '@mui/icons-material/Event';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LinkIcon from '@mui/icons-material/Link';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { tokens } from '../../../../global/theme/tokens';
import DateLabel from '../../../label/DateLabel';
import UserStack from '../../UserStack';
import { ProjectUserStack } from '../ProjectUserStack';
import RenderHTML from '../../../preview/RenderHTML';
import { getPriorityColorMap } from '../../../special/taskPriority';
import { checkDeadlinePassed } from '../../../special/checkDeadlinePassed';
import { SectionLabel } from './taskCardUi';
import TaskCardFiles from './TaskCardFiles';
import TaskCardLinks from './TaskCardLinks';
import TaskCardHistory from './TaskCardHistory';
import TaskCardSidebar from './TaskCardSidebar';

/**
 * Expandable task card.
 * Purely presentational for now - every mutation is reported through a
 * callback so the API wiring can be added later without touching the layout.
 */
const TaskCard = ({
  task,
  workPackage,
  defaultExpanded = false,
  onToggleComplete,
  isProcessing,
  onAssignSelf,
  isAssigning,
  editmode,
  projectId,
  onUsersChange,
  onFilesChange,
  onLinkAdd,
  onLinkDelete,
  onCommentSubmit,
  onEdit,
  onMove,
  onDelete,
  className = '',
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const [comment, setComment] = useState('');

  const priorityColorMap = getPriorityColorMap(colors);
  const isDone = Boolean(task.status);
  const hasUsers = task.users?.length > 0;
  const deadlinePassed = task.deadline && checkDeadlinePassed(task.deadline);

  function handleToggleOpen() {
    setIsOpen(!isOpen);
  }

  const takeOverButton = onAssignSelf ? (
    <Button
      size="small"
      variant="outlined"
      disabled={isAssigning}
      onClick={(e) => {
        // taking a task over must not expand it as well
        e.stopPropagation();
        onAssignSelf(task);
      }}
      startIcon={
        isAssigning ? (
          <span className="loader" style={{ width: '14px', height: '14px' }} />
        ) : (
          <PersonAddAltIcon sx={{ fontSize: '16px' }} />
        )
      }
      sx={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
    >
      {t('Take over')}
    </Button>
  ) : null;

  // in edit mode the stack opens the team dialog so members can be assigned,
  // otherwise it only lists who is on the task
  const usersSlot = editmode ? (
    <Box
      className="flex items-center gap-2"
      // the stack passes size to Avatar, which ignores it - the dimensions
      // have to come from the avatar styles
      sx={{
        '& .MuiAvatar-root': {
          width: '26px !important',
          height: '26px !important',
          fontSize: '11px',
        },
      }}
    >
      {!hasUsers && takeOverButton}
      <ProjectUserStack
        users={task.users || []}
        editmode
        noLabel
        displayCount={3}
        projectId={projectId}
        changeApi={`tasks/${task.id}/members`}
        onChange={() => onUsersChange && onUsersChange(task)}
      />
    </Box>
  ) : hasUsers ? (
    <Box
      sx={{
        '& .MuiAvatar-root': {
          width: '26px !important',
          height: '26px !important',
          fontSize: '11px',
        },
      }}
    >
      <UserStack users={task.users} max={3} />
    </Box>
  ) : (
    takeOverButton
  );

  // same arrow the todo list inside the work package uses. The header click
  // toggles too, so the button click must not reach it as well.
  const expandArrow = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleToggleOpen();
      }}
    >
      <ArrowDropDownIcon
        sx={{
          transform: `rotate(${isOpen ? 180 : 0}deg)`,
          transition: 'transform 200ms',
        }}
      />
    </IconButton>
  );

  function handleSubmitComment() {
    if (!comment.trim()) return;
    if (onCommentSubmit) onCommentSubmit(task, comment.trim());
    setComment('');
  }

  return (
    // no card around a task - the row sits directly on the surface below and
    // the list separates the rows with a thin line
    <Box className={`relative ${className}`}>
      {/* HEADER */}
      <Box
        className="relative flex items-start md:items-center gap-2 pl-4 pr-2 py-1.5 cursor-pointer"
        // the whole row expands the task, the buttons inside stop the click
        onClick={handleToggleOpen}
        sx={{
          borderBottom: isOpen ? `1px solid ${colors.text}12` : 'none',
          // a completed task is marked green when closed, but the color is removed when the details are open
          backgroundColor: isDone
            ? `${colors.success}30`
            : isOpen
              ? `${colors.text}06`
              : 'transparent',
          // the row expands on click, so it answers the hover
          ':hover': {
            backgroundColor: isDone ? `${colors.success}40` : `${colors.text}0c`,
          },
          transition: 'background-color 200ms',
        }}
      >
        {/* priority indicator, slimmed down with the rest of the card */}
        <Box
          className="absolute left-0 top-0 h-full"
          sx={{
            width: '4px',
            backgroundColor: priorityColorMap[task.priority],
          }}
        />

        <IconButton
          size="small"
          className="shrink-0"
          disabled={isProcessing}
          onClick={(e) => {
            // ticking a task off must not expand it as well
            e.stopPropagation();
            if (onToggleComplete) onToggleComplete(task);
          }}
        >
          {isProcessing ? (
            <span
              className="loader"
              style={{ width: '18px', height: '18px' }}
            />
          ) : isDone ? (
            <TaskAltIcon sx={{ fontSize: '20px', color: colors.success }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: '20px', opacity: 0.5 }} />
          )}
        </IconButton>

        <Box className="flex flex-col gap-0.5 grow min-w-0">
          <Typography
            fontSize="14px"
            fontWeight={600}
            sx={{ wordBreak: 'break-word', lineHeight: 1.25 }}
          >
            {task.name}
          </Typography>

          <Box className="flex items-center gap-x-2.5 gap-y-0.5 flex-wrap">
            {task.effortScore != null && (
              <Box className="flex items-center gap-1 opacity-70">
                <AdjustIcon sx={{ fontSize: '13px' }} />
                <Typography fontSize="11px">
                  {`${task.effortScore} ${t('Points')}`}
                </Typography>
              </Box>
            )}

            {task.deadline && (
              <Box
                className="flex items-center gap-1"
                sx={{
                  color: deadlinePassed && !isDone ? colors.error : 'inherit',
                  opacity: deadlinePassed && !isDone ? 1 : 0.7,
                }}
              >
                <EventIcon sx={{ fontSize: '13px' }} />
                <Typography fontSize="11px" fontWeight={500}>
                  <DateLabel date={task.deadline} />
                </Typography>
              </Box>
            )}

            {task.files?.length > 0 && (
              <Box className="flex items-center gap-1 opacity-70">
                <AttachFileIcon sx={{ fontSize: '13px' }} />
                <Typography fontSize="11px">{task.files.length}</Typography>
              </Box>
            )}

            {task.externalResources?.length > 0 && (
              <Box className="flex items-center gap-1 opacity-70">
                <LinkIcon sx={{ fontSize: '13px' }} />
                <Typography fontSize="11px">
                  {task.externalResources.length}
                </Typography>
              </Box>
            )}

            {/* mobile: the arrow closes the info row, the users follow below */}
            <Box className="flex md:hidden ml-auto">{expandArrow}</Box>
          </Box>

          {usersSlot && (
            <Box
              className="flex md:hidden justify-end"
              onClick={(e) => e.stopPropagation()}
            >
              {usersSlot}
            </Box>
          )}
        </Box>

        {usersSlot && (
          <Box
            className="hidden md:flex shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {usersSlot}
          </Box>
        )}

        <Box className="hidden md:flex shrink-0">{expandArrow}</Box>
      </Box>

      {/* BODY */}
      <Collapse in={isOpen} unmountOnExit>
        {/* on mobile the sidebar leads, on desktop it stays on the right */}
        <Box className="flex flex-col-reverse md:flex-row">
          <Box className="flex flex-col gap-4 p-4 grow min-w-0">
            {task.description && (
              <Box className="flex flex-col gap-1">
                <SectionLabel>{t('Description')}</SectionLabel>
                {/* the description is delivered as HTML */}
                <RenderHTML HTML={task.description} emptyHTML={' '} />
              </Box>
            )}

            <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TaskCardFiles
                files={task.files}
                onFilesChange={
                  onFilesChange ? (files) => onFilesChange(task, files) : null
                }
              />
              <TaskCardLinks
                resources={task.externalResources}
                onLinkAdd={(link) => onLinkAdd && onLinkAdd(task, link)}
                onLinkDelete={
                  onLinkDelete
                    ? (resource) => onLinkDelete(task, resource)
                    : null
                }
              />
            </Box>

            <TaskCardHistory history={task.history} />

            <Box
              className="flex items-center gap-2 rounded-lg px-3 py-2"
              sx={{ border: `1px solid ${colors.text}20` }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: '16px', opacity: 0.5 }} />
              <InputBase
                fullWidth
                value={comment}
                placeholder={t('Write a comment...')}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitComment();
                  }
                }}
                sx={{ fontSize: '13px' }}
              />
            </Box>
          </Box>

          <TaskCardSidebar
            task={task}
            workPackage={workPackage}
            onEdit={onEdit}
            onMove={onMove}
            onDelete={onDelete}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default TaskCard;
